import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { ThemePalette } from 'mui.theme';
import { clientThemePalette, CLIENT_THEME } from 'utils/clientTheme';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { keyframes } from '@emotion/react';
import type { Asset } from 'types/asset.types';
import {
  extractTireNotificationsForChart,
  isWithinLastHour,
  isSensorEnabled,
  getDynamicAssetTireInfo,
  extractTireLeakageNotificationsForChart,
} from 'views/TirePressure/util';
import { Chart } from './Chart';
import CompareContent from './CompareContent';
import NoData from './Components/Fallback/NoData';
import { VehicleSelector } from './Components/VehicleSelector';
import { SWButtonGroup } from 'components/Buttons/SWButtonGroup';
import { NotificationsTable } from './Components/VehicleInsightNotificationsTable';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { composeUniqueKey } from 'utils/helpers/string.manipulation';
import { YEAR_MONTH_DAY_TIME_FORMAT } from 'utils/constants/dateTime';
import CustomTimeZoneDateRangePicker from 'components/DatePIcker/CustomTimeZoneDateRangePicker';
import type { MakeModelDetails } from 'types/make-model.types';
import type {
  TirePattern,
  TirePatternPosition,
  TpmsNotification,
  TpmsLeakageNotification,
} from 'types/tire-pattern.types';
import { useTranslation } from 'react-i18next';

export const Content = ({
  enableDarkTheme,
  tireData,
  selectedAsset,
  limits,
  startDate,
  endDate,
  unitSystem,
  metricType,
  setMetricType,
  comparingAsset,
  assets,
  callOnFinalDateChange,
  selectedRange,
  setSelectedRange,
  handleDateChange,
  makeModelDetails,
  tirePatterns,
  customerId,
  batteryData,
  defaultProfile,
  clearProfile,
  defaultTireCode,
}: {
  enableDarkTheme: boolean;
  selected: string;
  setSelected: any;
  limits: any;
  tireData: any;
  selectedAsset: Asset | undefined;
  startDate: Date;
  endDate: Date;
  unitSystem: string;
  metricType: string;
  setMetricType: any;
  comparingAsset: string;
  assets: Asset[];
  callOnFinalDateChange: any;
  selectedRange: string;
  setSelectedRange: any;
  handleDateChange: any;
  latestTagData: any;
  makeModelDetails: MakeModelDetails[];
  tirePatterns: {
    tirePatterns: TirePattern[];
    tirePatternPositions: TirePatternPosition[];
  };
  customerId: string;
  batteryData: Record<
    string,
    Record<
      string,
      { isBatteryLow: boolean; voltageValue?: number; timeStamp?: number }
    >
  >;
  defaultProfile: string | null;
  clearProfile: () => void;
  defaultTireCode?: string | null;
}) => {
  const { t } = useTranslation();
  const chartKey = 'avg_val';
  const colorMode = enableDarkTheme ? 'dark' : 'light';
  const themeColors = clientThemePalette[CLIENT_THEME][colorMode];
  const [trigger, setTrigger] = useState(0);
  const [, setCompleted] = useState<number>(0);
  const { preferredTimeDisplayMode, timezone } = useTimeDisplayMode();
  const location = useLocation();
  const { navType } = location.state || {};
  const handleCallChildFunction = () => {
    setTrigger((prev) => prev + 1);
  };

  const handleChildComplete = () => {
    setCompleted((prev) => prev + 1);
  };

  useEffect(() => {
    if (navType === 'Temperature' || navType === 'Pressure') {
      setMetricType(navType);
    }
  }, [navType]);

  let assetTireData = {
    pressureData: {},
    notifications: [],
    latestTireTimeStamp: 0,
    tireFitmentData: [],
    fitmentHistory: [],
    leakageNotifications: [],
  };

  if (selectedAsset) {
    assetTireData = tireData[selectedAsset?.device.deviceId] ?? {
      pressureData: {},
      notifications: [],
      latestTireTimeStamp: 0,
      tireFitmentData: [],
      fitmentHistory: [],
      leakageNotifications: [],
    };
  }

  const hasData = (tagData: any) => {
    if (Object.values(tireData).length === 0 || !tagData.pressureData) {
      return false;
    }
    return Object.values(tagData.pressureData).some(
      (array: any) => array.length > 0
    );
  };

  const availableProfiles = Object.keys(assetTireData.pressureData || {});
  let defaultProfiles = availableProfiles;
  if (defaultProfile) {
    defaultProfiles = [defaultProfile];
  }
  const [profiles, setProfiles] = useState<string[]>(defaultProfiles);

  const colors = [
    '#039161',
    '#59BFAF',
    '#F08733',
    '#327FF1',
    '#D301D7',
    '#EB5758',
    '#FFB300',
    '#8E44AD',
    '#00ACC1',
    '#6D4C41',
  ];

  let tireCodes: Record<string, string> = {};

  if (selectedAsset) {
    const assetTireDetails = getDynamicAssetTireInfo(
      makeModelDetails,
      tirePatterns.tirePatternPositions,
      selectedAsset.make,
      selectedAsset.model,
      customerId
    );
    tireCodes = assetTireDetails.tireCodes;
  }

  const isDataPresent = hasData(assetTireData);
  const showLiveButton = isWithinLastHour(
    assetTireData?.latestTireTimeStamp ?? 0
  );

  const filteredNotifications =
    assetTireData?.notifications?.filter((item: TpmsNotification) => {
      const hasTemperature = item?.rule?.ruleName
        ?.toLowerCase()
        .includes('temperature');
      const matchesMetricType =
        metricType === 'Temperature' ? hasTemperature : !hasTemperature;

      if (!matchesMetricType) return false;

      const tagName = item?.rule?.ruleConditionAssociations?.[0]?.tagName ?? '';
      const parts = tagName.split('_');
      const notificationTireCode = (parts[2] || '').toLowerCase();

      // Filter by the set of active profiles (case-insensitive)
      return profiles.some((profile) => {
        return notificationTireCode === profile.toLowerCase();
      });
    }) ?? [];

  const filteredLeakageNotifications =
    assetTireData?.leakageNotifications.filter(
      (item: TpmsLeakageNotification) => {
        const notificationTireCode = item.tireCode.toLowerCase();
        return profiles.some((profile) => {
          return notificationTireCode === profile.toLowerCase();
        });
      }
    );

  let notificationData = extractTireNotificationsForChart(
    filteredNotifications || []
  );

  const leakageData = extractTireLeakageNotificationsForChart(
    filteredLeakageNotifications || []
  );

  notificationData = [...notificationData, ...leakageData];

  const blink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
`;

  const assetHasTpmSSensor = isSensorEnabled(assetTireData);

  return (
    <>
      {!assetHasTpmSSensor ? (
        <Typography
          sx={{
            marginTop: '10px',
            color: themeColors.textPrimary,
            fontSize: '14px',
            padding: '10px',
            border: '1px solid #2F445E',
            borderRadius: '4px',
          }}
        >
          {t('tpms_sensor_not_configured_for_asset')}
        </Typography>
      ) : (
        <>
          <Box
            sx={{
              marginTop: '20px',
              backgroundColor: enableDarkTheme
                ? themeColors.toolBarBackground
                : themeColors.toolBarBackground,
              padding: '10px',
              borderRadius: '4px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '10px',
              }}
            >
              <Typography
                sx={{
                  color: ThemePalette.typography.grey,
                  fontSize: '12px',
                  marginRight: '10px',
                }}
              >
                {t('quick_range_10')}
              </Typography>
              <SWButtonGroup
                selected={selectedRange}
                setSelected={setSelectedRange}
                enableDarkTheme={enableDarkTheme}
                options={['Today', '1D', '7D', '14D', '1M', 'Custom']}
                clearProfile={clearProfile}
              />
              {selectedRange === 'Custom' && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginLeft: '10px',
                  }}
                >
                  <CustomTimeZoneDateRangePicker
                    startDate={startDate}
                    endDate={endDate}
                    onFinalDateChange={handleDateChange}
                    maxDate={dayjs()}
                    sx={{
                      minWidth: '148px',
                      maxWidth: '148px',
                      width: '148px',
                    }}
                    disabled={selectedRange !== 'Custom'}
                    maxDaysFromStartDate={30}
                  />
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'end',
                    }}
                  >
                    <Button
                      size="small"
                      disabled={!startDate || !endDate}
                      sx={{
                        marginLeft: '10px',
                        border: `1px solid ${themeColors.primary}`,
                        color: themeColors.textPrimary,
                        borderRadius: '5px',
                        textTransform: 'none',
                      }}
                      onClick={() => {
                        callOnFinalDateChange(endDate);
                      }}
                    >
                      {t('apply')}
                    </Button>
                  </div>
                </div>
              )}
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignContent: 'center',
              }}
            >
              <Button
                size="small"
                sx={{
                  border: `1px solid ${themeColors.primary}`,
                  color: themeColors.textPrimary,
                  borderRadius: '5px',
                  paddingLeft: '15px',
                  paddingRight: '15px',
                  textTransform: 'none',
                  marginRight: '10px',
                  marginTop: '2px',
                  marginBottom: '10px',
                }}
                onClick={() => {
                  setMetricType(
                    metricType === 'Pressure' ? 'Temperature' : 'Pressure'
                  );
                }}
              >
                {metricType === 'Pressure'
                  ? 'View Temperature Data'
                  : 'View Pressure Data'}
              </Button>
            </Box>

            <Grid container spacing={2}>
              <Grid item lg={5} sm={12} md={6} xs={12}>
                <Box
                  sx={{
                    border: '1px solid #2F445E',
                    borderRadius: '4px',
                    padding: '10px 10px 5px 10px',
                    height: '710px',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      mb: 1,
                    }}
                  >
                    {/* Left Side: Title */}
                    <Typography
                      sx={{
                        color: themeColors.textPrimary,
                        fontSize: '14px',
                        fontWeight: '700',
                      }}
                    >
                      {`${metricType} Trend for ${
                        selectedAsset ? selectedAsset.bumperNumber : ''
                      }`}
                    </Typography>

                    {/* Right Side: Buttons */}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        sx={{
                          backgroundColor: ThemePalette.typography.primary,
                          color: ThemePalette.typography.white,
                          textTransform: 'none',
                          height: '40px',
                          paddingLeft: '15px',
                          paddingRight: '15px',
                          fontSize: '12px',
                          border: `1px solid white`,
                          '&:hover': {
                            color: enableDarkTheme ? 'white' : 'black',
                            border: enableDarkTheme
                              ? '1px solid white'
                              : '1px solid black',
                          },
                        }}
                        onClick={handleCallChildFunction}
                      >
                        {t('export_to_csv')}
                      </Button>
                    </Box>
                  </Box>

                  <Box height={'500px'} marginTop={'8px'} width={'100%'}>
                    {isDataPresent ? (
                      <Chart
                        enableDarkTheme={enableDarkTheme}
                        data={assetTireData}
                        colors={colors}
                        allowedProfiles={profiles}
                        unitSystem={unitSystem}
                        tireCodes={tireCodes}
                        metricType={metricType}
                        setMetricType={setMetricType}
                        notifications={notificationData}
                        startDate={startDate}
                        endDate={endDate}
                        selectedAsset={selectedAsset}
                        trigger={trigger}
                        onComplete={handleChildComplete}
                        chartKey={chartKey}
                        customerId={customerId}
                        makeModelDetails={makeModelDetails}
                        tirePatterns={tirePatterns}
                        selectedRange={selectedRange}
                      />
                    ) : (
                      <NoData
                        text={`No data available for asset ${selectedAsset?.bumperNumber}.`}
                        textFontSize={'16px'}
                      />
                    )}
                    <Box
                      sx={{
                        textAlign: 'center',
                      }}
                    >
                      {Object.keys(assetTireData?.pressureData)
                        .filter((item) => tireCodes[item])
                        .map((item, index) => {
                          const values = Object.values(
                            assetTireData?.pressureData
                          );
                          // @ts-expect-error ignoring for checks
                          const hasNoData = values[index].length === 0;

                          return (
                            <FormControlLabel
                              key={index}
                              label={
                                <span
                                  style={{
                                    fontSize: '12px',
                                    color: hasNoData
                                      ? ThemePalette.typography.lightGrey
                                      : colors[index],
                                  }}
                                >
                                  {tireCodes[
                                    '0x' +
                                      item
                                        .toLowerCase()
                                        .replace('0x', '')
                                        .toUpperCase()
                                  ] ?? item}
                                </span>
                              }
                              disabled={hasNoData}
                              control={
                                <Checkbox
                                  size="small"
                                  sx={{
                                    color: hasNoData
                                      ? ThemePalette.typography.lightGrey
                                      : colors[index],
                                    '&.Mui-checked': {
                                      color: hasNoData
                                        ? ThemePalette.typography.lightGrey
                                        : colors[index],
                                    },
                                    '&.Mui-disabled': {
                                      color: ThemePalette.typography.lightGrey,
                                    },
                                    '&.Mui-disabled.Mui-checked': {
                                      color: ThemePalette.typography.lightGrey,
                                    },
                                    '& svg': {
                                      fontSize: '16px',
                                    },
                                  }}
                                  checked={
                                    hasNoData ? false : profiles.includes(item)
                                  }
                                  disabled={hasNoData}
                                  onChange={() => {
                                    if (hasNoData) {
                                      return;
                                    }
                                    if (profiles.includes(item)) {
                                      setProfiles(
                                        profiles.filter((e) => e !== item)
                                      );
                                    } else {
                                      setProfiles([...profiles, item]);
                                    }
                                  }}
                                />
                              }
                              sx={{
                                color: hasNoData
                                  ? ThemePalette.typography.lightGrey
                                  : colors[index],
                              }}
                            />
                          );
                        })}
                    </Box>
                  </Box>
                </Box>
              </Grid>
              <Grid item lg={7} sm={12} md={6} xs={12}>
                <Box
                  sx={{
                    border: '1px solid #2E435E',
                    padding: '10px',
                    borderRadius: '4px',
                    height: '705px',
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    {showLiveButton ? (
                      <Button
                        sx={{
                          backgroundColor: '#133063',
                          color: 'white',
                          textTransform: 'none',
                          borderRadius: '999px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          '&:hover': {
                            backgroundColor: '#133063',
                          },
                          paddingLeft: '20px',
                          paddingRight: '20px',
                          width: '100px',
                          cursor: 'none',
                        }}
                        disableRipple
                      >
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            backgroundColor: 'red',
                            animation: `${blink} 1s infinite`,
                          }}
                        />
                        <Typography
                          fontWeight="bold"
                          sx={{
                            fontSize: '14px',
                          }}
                        >
                          {t('dm_log_live')}
                        </Typography>
                      </Button>
                    ) : (
                      <Typography
                        sx={{
                          fontSize: '10px',
                          color: themeColors.textPrimary,
                        }}
                      >
                        {' '}
                        {t('last_updated')}
                        <span
                          style={{
                            marginLeft: '5px',
                          }}
                        >
                          {' '}
                          {convertUtcToDisplayTime(
                            assetTireData?.latestTireTimeStamp,
                            preferredTimeDisplayMode,
                            timezone,
                            YEAR_MONTH_DAY_TIME_FORMAT
                          )}
                        </span>
                      </Typography>
                    )}
                  </Box>
                  <Grid container spacing={2}>
                    <VehicleSelector
                      asset={selectedAsset}
                      tireData={assetTireData}
                      limits={limits}
                      assetType={selectedAsset?.assetType ?? ''}
                      enableDarkTheme={enableDarkTheme}
                      unitSystem={unitSystem}
                      tireCodes={tireCodes}
                      metricType={metricType}
                      makeModel={
                        selectedAsset?.make && selectedAsset?.model
                          ? composeUniqueKey(
                              [selectedAsset.make, selectedAsset.model],
                              '-'
                            )
                          : ''
                      }
                      customerId={customerId}
                      makeModelDetails={makeModelDetails}
                      tirePatterns={tirePatterns}
                      batteryData={batteryData}
                      selectedAsset={selectedAsset}
                      onTireClick={(tireCode) => {
                        if (tireCode) {
                          setProfiles([tireCode]);
                        }
                      }}
                    />
                  </Grid>
                </Box>
              </Grid>
            </Grid>

            <Box
              sx={{
                border: '1px solid #2F445E',
                marginTop: '10px',
                borderRadius: '4px',
              }}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  color: themeColors.textPrimary,
                  fontWeight: '700',
                  padding: '10px',
                  marginTop: '10px',
                }}
              >
                {t('notification_history')}
              </Typography>

              {assetTireData?.notifications?.length === 0 &&
              assetTireData?.leakageNotifications?.length === 0 ? (
                <Typography
                  sx={{
                    fontSize: '12px',
                    color: themeColors.textPrimary,
                    paddingLeft: '10px',
                    marginBottom: '10px',
                  }}
                >
                  {t('no_notifications_available_2')}
                </Typography>
              ) : (
                <NotificationsTable
                  enableDarkTheme={enableDarkTheme}
                  notifications={filteredNotifications}
                  customerId={customerId}
                  makeModelDetails={makeModelDetails}
                  tirePatterns={tirePatterns}
                  leakageNotifications={filteredLeakageNotifications}
                  tireCodes={tireCodes}
                />
              )}
            </Box>
          </Box>

          {comparingAsset !== '' && (
            <Box
              sx={{
                marginTop: '20px',
                backgroundColor: enableDarkTheme
                  ? themeColors.toolBarBackground
                  : themeColors.toolBarBackground,
                padding: '10px',
                borderRadius: '4px',
              }}
            >
              <CompareContent
                enableDarkTheme={enableDarkTheme}
                comparingAsset={comparingAsset}
                assets={assets}
                tireData={tireData}
                colors={colors}
                unitSystem={unitSystem}
                tireCodes={tireCodes}
                metricType={metricType}
                setMetricType={setMetricType}
                limits={limits}
                startDate={startDate}
                endDate={endDate}
                selectedValue={chartKey}
                customerId={customerId}
                makeModelDetails={makeModelDetails}
                tirePatterns={tirePatterns}
                batteryData={batteryData}
                selectedRange={selectedRange}
              />
            </Box>
          )}
        </>
      )}
    </>
  );
};
